import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import MainLayout from './layout/MainLayout.jsx';
import { initGA, trackPageView } from './lib/ga.js';
import { useAuth } from './lib/auth.jsx';
import { hasAccess } from './lib/rbac.js';
import { lazy, Suspense } from 'react';
import ComingSoon from './components/ComingSoon.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import {
  COOKIE_CONSENT_EVENT,
  readCookieConsent,
} from './lib/consent.js';

import { ErrorBoundary } from './components/ErrorBoundary.jsx';

const Login = lazy(() => import('./pages/Login.jsx'));
const LoginCallback = lazy(() => import('./pages/LoginCallback.jsx'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Cadastro = lazy(() => import('./pages/Cadastro.jsx'));
const Detalhe = lazy(() => import('./pages/Detalhe.jsx'));
const PessoaForm = lazy(() => import('./pages/PessoaForm.jsx'));
const ContasReceber = lazy(() => import('./pages/ContasReceber.jsx'));
const ContasPagar = lazy(() => import('./pages/ContasPagar.jsx'));
const Extrato = lazy(() => import('./pages/Extrato.jsx'));
const FluxoCaixa = lazy(() => import('./pages/FluxoCaixa.jsx'));
const Lancamentos = lazy(() => import('./pages/Lancamentos.jsx'));
const DreGerencial = lazy(() => import('./pages/DreGerencial.jsx'));
const Fechamentos = lazy(() => import('./pages/Fechamentos.jsx'));
const Contas = lazy(() => import('./pages/Contas.jsx'));
const Cartoes = lazy(() => import('./pages/Cartoes.jsx'));
const Relatorios = lazy(() => import('./pages/Relatorios.jsx'));
const Aprovacoes = lazy(() => import('./pages/Aprovacoes.jsx'));
const Retroativos = lazy(() => import('./pages/Retroativos.jsx'));
const Funcionarios = lazy(() => import('./pages/Funcionarios.jsx'));
const Configuracoes = lazy(() => import('./pages/Configuracoes.jsx'));
const MeuPerfil = lazy(() => import('./pages/MeuPerfil.jsx'));
const Socios = lazy(() => import('./pages/Socios.jsx'));
const Monitoramento = lazy(() => import('./pages/Monitoramento.jsx'));
const Integracoes = lazy(() => import('./pages/Integracoes.jsx'));


// Guard: exige sessão e, opcionalmente, nível mínimo de acesso.
function Protected({ children, minLevel }) {
  const { user, level, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-text-muted">Carregando…</div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!hasAccess(level, minLevel)) return <Navigate to="/" replace />;
  return children;
}

// Rota legada /financeiro (reestruturação Tesouraria/Controladoria): deep links
// antigos com ?pessoa_id/?categoria caem em /lancamentos; o resto vai para o DRE.
function FinanceiroRedirect() {
  const [searchParams] = useSearchParams();
  const isLancamentos = searchParams.get('pessoa_id') || searchParams.get('categoria');
  const qs = searchParams.toString();
  return <Navigate to={`${isLancamentos ? '/lancamentos' : '/dre'}${qs ? `?${qs}` : ''}`} replace />;
}

// Alias legado /minha-area → /meu-perfil (preserva ?tab= e /reembolsos).
function MinhaAreaRedirect() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  if (pathname.includes('/reembolsos')) {
    return <Navigate to="/meu-perfil?tab=reembolsos" replace />;
  }
  if (tab) {
    return <Navigate to={`/meu-perfil?tab=${encodeURIComponent(tab)}`} replace />;
  }
  return <Navigate to="/meu-perfil" replace />;
}

// GA4: inicializa uma vez e envia page_view a cada troca de rota (SPA).
// Sem VITE_GA_ID (dev/hml) os eventos caem em console.debug — ver lib/ga.js.
function AnalyticsTracker() {
  const location = useLocation();
  const [consent, setConsent] = useState(readCookieConsent);

  useEffect(() => {
    const updateConsent = (event) => setConsent(event.detail);
    window.addEventListener(COOKIE_CONSENT_EVENT, updateConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, updateConsent);
  }, []);
  useEffect(() => {
    if (consent === 'accepted') initGA();
  }, [consent]);
  useEffect(() => {
    if (consent === 'accepted') trackPageView(location.pathname + location.search);
  }, [consent, location]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AnalyticsTracker />
    <Suspense fallback={<div className="min-h-screen grid place-items-center text-text-muted">Carregando...</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/privacidade" element={<PoliticaPrivacidade />} />

        <Route
          element={
            <Protected>
              <MainLayout />
            </Protected>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="cadastro" element={<Cadastro />} />
          <Route path="cadastro/novo" element={<PessoaForm />} />
          <Route path="cadastro/:id" element={<Detalhe />} />
          <Route path="cadastro/:id/editar" element={<PessoaForm />} />
          <Route path="financeiro" element={<FinanceiroRedirect />} />
          <Route
            path="contas-receber"
            element={
              <Protected minLevel="financeiro">
                <ContasReceber />
              </Protected>
            }
          />
          <Route
            path="contas-pagar"
            element={
              <Protected minLevel="financeiro">
                <ContasPagar />
              </Protected>
            }
          />
          <Route
            path="extrato"
            element={
              <Protected minLevel="financeiro">
                <Extrato />
              </Protected>
            }
          />
          <Route
            path="fluxo-caixa"
            element={
              <Protected minLevel="financeiro">
                <FluxoCaixa />
              </Protected>
            }
          />
          <Route
            path="lancamentos"
            element={
              <Protected minLevel="financeiro">
                <Lancamentos />
              </Protected>
            }
          />
          <Route
            path="dre"
            element={
              <Protected minLevel="financeiro">
                <DreGerencial />
              </Protected>
            }
          />
          <Route
            path="fechamentos"
            element={
              <Protected minLevel="financeiro">
                <Fechamentos />
              </Protected>
            }
          />
          <Route
            path="contas"
            element={
              <Protected minLevel="financeiro">
                <Contas />
              </Protected>
            }
          />
          <Route
            path="aprovacoes"
            element={
              <Protected minLevel="financeiro">
                <Aprovacoes />
              </Protected>
            }
          />
          <Route
            path="cartoes"
            element={
              <Protected minLevel="financeiro">
                <Cartoes />
              </Protected>
            }
          />
          <Route
            path="relatorios"
            element={
              <Protected minLevel="financeiro">
                <Relatorios />
              </Protected>
            }
          />
          <Route
            path="retroativos"
            element={
              <Protected minLevel="financeiro">
                <Retroativos />
              </Protected>
            }
          />
          <Route
            path="funcionarios"
            element={
              <Protected minLevel="gestor">
                <Funcionarios />
              </Protected>
            }
          />
          <Route
            path="socios"
            element={
              <Protected minLevel="financeiro">
                <Socios />
              </Protected>
            }
          />
          <Route
            path="configuracoes"
            element={
              <Protected minLevel="admin">
                <Configuracoes />
              </Protected>
            }
          />
          <Route
            path="sistemas/monitoramento"
            element={
              <Protected minLevel="admin">
                <Monitoramento />
              </Protected>
            }
          />
          <Route
            path="sistemas/integracoes"
            element={
              <Protected minLevel="admin">
                <Integracoes />
              </Protected>
            }
          />
          <Route path="ia-chatbot" element={<Protected minLevel="admin"><ComingSoon moduleName="IA & Chatbot" /></Protected>} />
          <Route path="rh-pdi" element={<ComingSoon moduleName="RH Avançado / PDI" />} />
          <Route path="asaas-massa" element={<Protected minLevel="financeiro"><ComingSoon moduleName="Disparo em Massa Asaas" /></Protected>} />
          <Route path="meu-perfil" element={<MeuPerfil />} />
          <Route path="minha-area" element={<MinhaAreaRedirect />} />
          <Route path="minha-area/*" element={<MinhaAreaRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    <CookieConsent />
    </ErrorBoundary>
  );
}
